import React from 'react';
import { Link } from 'react-router-dom';
import { Food } from '../../db/foodData';
import { getImageURLRemoveSpaces, handleImageError } from '../../helperFunctions/getImageURL';
import { getForkTypeColor } from './ForkType';
import TableBar, { FoodTableBarType } from './Bar';
import ForkIcon from '@mui/icons-material/Restaurant';
import BiomeTag from '../Tags/BiomeTag';
import DurationTag from '../Tags/DurationTag';
import CraftingStationCell from './CraftingStationCell';
import { maxHealth, maxStamina, maxEitr, maxTotal } from '../../App';
import './FoodCard.css';

interface FoodCardProps {
  food: Food;
}

const FoodCard: React.FC<FoodCardProps> = ({ food }) => {
  return (
    <div className="food-card">
      <div className="food-card-header">
        <img 
          src={getImageURLRemoveSpaces(food.icon)} 
          alt={food.name}
          onError={handleImageError}
          className="food-card-icon"
        />
        <div className="food-card-title">
          <Link to={`/${food.name.toLowerCase()}`} className="food-card-name">
            {food.name}
          </Link>
          <div className="food-card-fork">
            <ForkIcon sx={{ color: getForkTypeColor(food.forkType), fontSize: 18 }} />
          </div>
        </div>
      </div>

      <div className="food-card-stats">
        <div className="food-card-stat">
          <span className="food-card-stat-label">Health</span>
          <TableBar value={food.health} maxValue={maxHealth} type={FoodTableBarType.Health} />
        </div>
        <div className="food-card-stat">
          <span className="food-card-stat-label">Stamina</span>
          <TableBar value={food.stamina} maxValue={maxStamina} type={FoodTableBarType.Stamina} />
        </div>
        {food.eitr > 0 && (
          <div className="food-card-stat">
            <span className="food-card-stat-label">Eitr</span>
            <TableBar value={food.eitr} maxValue={maxEitr} type={FoodTableBarType.Eitr} />
          </div>
        )}
        <div className="food-card-stat">
          <span className="food-card-stat-label">Total</span>
          <TableBar value={food.total} maxValue={maxTotal} type={FoodTableBarType.Total} />
        </div>
      </div>

      <div className="food-card-details">
        {food.rawMaterials.length > 0 && (
          <div className="food-card-detail-row">
            <span className="food-card-detail-label">Materials</span>
            <span className="food-card-detail-value">{food.rawMaterials.join(', ')}</span>
          </div>
        )}
        <div className="food-card-detail-row">
          <span className="food-card-detail-label">Duration</span>
          <DurationTag duration={food.duration} />
        </div>
        {food.weight > 0 && (
          <div className="food-card-detail-row">
            <span className="food-card-detail-label">Weight</span>
            <span className="food-card-detail-value">{food.weight}</span>
          </div>
        )}
        <div className="food-card-detail-row">
          <span className="food-card-detail-label">Stack</span>
          <span className="food-card-detail-value">{food.stackSize}</span>
        </div>
      </div>

      <div className="food-card-tags">
        <BiomeTag biome={food.biomeProgression} link={false} />
        <CraftingStationCell 
          cell={{ 
            craftingStation: food.craftingStation, 
            craftingStationLevel: food.craftingStationLevel 
          }} 
        />
      </div>
    </div>
  );
};

export default FoodCard;
